/**
 * LabeledField - Field display component with change highlighting
 *
 * Renders a field with its label and value in one of several modes:
 * - View: Read-only display
 * - Edit: Editable input
 * - Update: Old value → new value
 * - Create: Just the new value
 */

import { ArrowRight } from 'lucide-react';
import React from 'react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { FilevineSectionFieldConfigSchema } from '@/services/api/generated/entitySyncService';

import type { FilevineSectionFieldValue } from '../utils/fieldEditors';
import { isFieldEditable } from '../utils/fieldEditors';
import {
  type ParsedSuggestedValue,
  parseSuggestedValue,
} from '../utils/fieldMatching';
import { FieldValue } from '../utils/fieldRenderers';
import { EditableFieldValue } from './EditableFieldValue';
import { LinkedContactPreview } from './LinkedContactPreview';

// ============================================================================
// Types
// ============================================================================

export interface LabeledFieldProps {
  label: string;
  value: unknown;
  fieldType: number;
  fieldConfig: FilevineSectionFieldConfigSchema;
  isRequired?: boolean;
  hasChange?: boolean;
  suggestedValue?: unknown;
  isCreate?: boolean;
  editable?: boolean;
  onValueChange?: (value: FilevineSectionFieldValue) => void;
  firmId?: string;
}

/** Display mode for the field */
type FieldDisplayMode = 'view' | 'edit' | 'update' | 'create';

// ============================================================================
// Helper Components - Each handles one display mode
// ============================================================================

/** Render a suggested value with appropriate styling */
export const SuggestedValueDisplay: React.FC<{
  parsed: ParsedSuggestedValue;
  firmId?: string;
  className?: string;
}> = ({ parsed, firmId, className }) => {
  if (parsed.type === 'contact_link' && firmId) {
    return (
      <LinkedContactPreview contactId={parsed.contactId} firmId={firmId} />
    );
  }
  return (
    <span className={className ?? 'font-medium text-zinc-900'}>
      {parsed.text}
    </span>
  );
};

/** View mode: read-only display */
const ViewModeContent: React.FC<{ value: unknown; fieldType: number }> = ({
  value,
  fieldType,
}) => (
  <div className="text-sm">
    <FieldValue value={value} fieldType={fieldType} />
  </div>
);

/** Edit mode: editable input */
const EditModeContent: React.FC<{
  value: unknown;
  fieldType: number;
  fieldConfig: FilevineSectionFieldConfigSchema;
  onChange: (value: FilevineSectionFieldValue) => void;
}> = ({ value, fieldType, fieldConfig, onChange }) => (
  <EditableFieldValue
    value={value}
    fieldType={fieldType}
    fieldConfig={fieldConfig}
    onChange={onChange}
  />
);

/** Update mode: old value struck through, arrow, new value */
const UpdateModeContent: React.FC<{
  oldValue: unknown;
  fieldType: number;
  parsed: ParsedSuggestedValue;
  firmId?: string;
}> = ({ oldValue, fieldType, parsed, firmId }) => (
  <div className="space-y-2">
    <div className="flex items-center gap-2 text-sm flex-wrap">
      <span className="text-zinc-500 line-through">
        <FieldValue value={oldValue} fieldType={fieldType} />
      </span>
      <ArrowRight className="h-3 w-3 text-amber-600 flex-shrink-0" />
    </div>
    <SuggestedValueDisplay parsed={parsed} firmId={firmId} />
  </div>
);

/** Create mode: just the new value */
const CreateModeContent: React.FC<{
  parsed: ParsedSuggestedValue;
  firmId?: string;
}> = ({ parsed, firmId }) => (
  <div className="text-sm">
    <SuggestedValueDisplay parsed={parsed} firmId={firmId} />
  </div>
);

/** Status badge shown next to label */
const FieldStatusBadge: React.FC<{
  editable: boolean;
  canEdit: boolean;
  hasChange: boolean;
  isCreate: boolean;
}> = ({ editable, canEdit, hasChange, isCreate }) => {
  if (hasChange) {
    return (
      <Badge
        variant="outline"
        className="flex-shrink-0 border-amber-400 bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0"
      >
        {isCreate ? 'Will create' : 'Will update'}
      </Badge>
    );
  }
  if (editable && !canEdit) {
    return (
      <Badge
        variant="outline"
        className="flex-shrink-0 border-zinc-300 bg-zinc-100 text-zinc-500 text-[10px] px-1.5 py-0"
      >
        Read-only
      </Badge>
    );
  }
  return null;
};

// ============================================================================
// Main Component
// ============================================================================

export const LabeledField: React.FC<LabeledFieldProps> = ({
  label,
  value,
  fieldType,
  fieldConfig,
  isRequired,
  hasChange,
  suggestedValue,
  isCreate,
  editable,
  onValueChange,
  firmId,
}) => {
  // Determine display mode
  const canEdit = Boolean(editable) && isFieldEditable(fieldType);
  const parsed = parseSuggestedValue(suggestedValue);

  const mode: FieldDisplayMode = (() => {
    if (canEdit && onValueChange) return 'edit';
    if (hasChange && !isCreate && value != null) return 'update';
    if (hasChange && isCreate) return 'create';
    return 'view';
  })();

  // Value to use in edit mode
  const editableValue = suggestedValue !== undefined ? suggestedValue : value;

  return (
    <div
      className={cn(
        'space-y-1.5 rounded-md px-2 py-2 -mx-2',
        hasChange && 'bg-amber-50 ring-1 ring-amber-200',
      )}
    >
      {/* Label row */}
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-medium text-zinc-500 flex items-center gap-1">
          {label}
          {isRequired && <span className="text-red-500">*</span>}
        </div>
        <FieldStatusBadge
          editable={editable ?? false}
          canEdit={canEdit}
          hasChange={hasChange ?? false}
          isCreate={isCreate ?? false}
        />
      </div>

      {/* Content by mode */}
      {mode === 'edit' && onValueChange && (
        <EditModeContent
          value={editableValue}
          fieldType={fieldType}
          fieldConfig={fieldConfig}
          onChange={onValueChange}
        />
      )}
      {mode === 'update' && (
        <UpdateModeContent
          oldValue={value}
          fieldType={fieldType}
          parsed={parsed}
          firmId={firmId}
        />
      )}
      {mode === 'create' && (
        <CreateModeContent parsed={parsed} firmId={firmId} />
      )}
      {mode === 'view' && (
        <ViewModeContent value={value} fieldType={fieldType} />
      )}
    </div>
  );
};

export default LabeledField;
